import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Container, VStack, HStack, Heading, Text, Box, Badge, Divider, Button, Link,
  SimpleGrid, Icon, Table, Thead, Tbody, Tr, Th, Td, TableContainer,
  useColorModeValue, Stack, Tag, TagLabel,
} from "@chakra-ui/react";
import {
  FiArrowLeft, FiArrowRight, FiCpu, FiMessageCircle, FiZap, FiShield,
  FiLayers, FiTrendingUp, FiServer, FiCheckCircle, FiDollarSign, FiRepeat,
} from "react-icons/fi";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";

/**
 * Public, shareable strategy page for HQ Autopilot — the "business-in-a-box" venture.
 * Publishes the hardware costs + funding stages + venture economics from
 * biz_in_a_folder_tommillerservices/clients/tms/plans/claude_sessions/2026-07-09-local-ai-scale-out-and-biz-in-a-box.md
 * No auth — safe to share. Mobile-first (single column below md).
 */
const HQAutopilot: React.FC = () => {
  const pageBg = useColorModeValue("gray.50", "gray.900");
  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "whiteAlpha.200");
  const heroBg = useColorModeValue(
    "linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)",
    "linear-gradient(135deg, rgba(49,46,129,0.35) 0%, rgba(30,27,75,0.5) 100%)"
  );
  const textPrimary = useColorModeValue("gray.800", "gray.100");
  const textSecondary = useColorModeValue("gray.600", "gray.300");
  const textMuted = useColorModeValue("gray.500", "gray.400");
  const accent = useColorModeValue("purple.600", "purple.300");

  const Card = ({ children, ...rest }: any) => (
    <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="xl"
      p={{ base: 5, md: 6 }} boxShadow="sm" {...rest}>{children}</Box>
  );

  const Feature = ({ icon, title, children, color = "purple" }: any) => (
    <Box bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="lg" p={5}>
      <HStack mb={2} spacing={3}>
        <Icon as={icon} boxSize={5} color={`${color}.400`} />
        <Text fontWeight="bold" color={textPrimary}>{title}</Text>
      </HStack>
      <Text fontSize="sm" color={textSecondary} lineHeight="1.6">{children}</Text>
    </Box>
  );

  return (
    <Box bg={pageBg} minH="100vh">
      <NavbarWithCallToAction />

      {/* ── Hero ───────────────────────────────────────────── */}
      <Box bg={heroBg}>
        <Container maxW="container.lg" py={{ base: 10, md: 16 }}>
          <VStack spacing={5} align="start">
            <Link as={RouterLink} to="/" _hover={{ textDecoration: "none" }}>
              <Button variant="ghost" size="sm" leftIcon={<FiArrowLeft />} color={textMuted}>Home</Button>
            </Link>
            <HStack spacing={2} flexWrap="wrap">
              <Badge colorScheme="purple" fontSize="sm" px={2} py={1} borderRadius="md">HQ Autopilot</Badge>
              <Badge colorScheme="blue">Strategy & costs</Badge>
              <Tag size="sm" colorScheme="green" variant="subtle"><TagLabel>Pilot #1 live</TagLabel></Tag>
              <Text fontSize="xs" color={textMuted}>Updated 9 Jul 2026</Text>
            </HStack>
            <Heading size={{ base: "xl", md: "2xl" }} color={textPrimary} lineHeight="1.1">
              An AI that runs the business, so the owner doesn't have to.
            </Heading>
            <Text fontSize={{ base: "md", md: "xl" }} color={textSecondary} maxW="3xl" lineHeight="1.6">
              Every small business gets <strong>their own HQ</strong> and an AI that actually does the daily
              back-office — admin, scheduling, invoicing, comms, reporting. The owner just talks to a chat box;
              the AI does the work and surfaces it for one approval. <strong>A business in a box.</strong>
            </Text>
            <Text fontSize="sm" color={textMuted}>
              This page publishes the full plan behind it — the hardware, the real costs, and the funding stages —
              openly, so partners and investors can see the thinking.
            </Text>
          </VStack>
        </Container>
      </Box>

      <Container maxW="container.lg" py={{ base: 8, md: 12 }}>
        <VStack spacing={{ base: 10, md: 14 }} align="stretch">

          {/* ── The experience ─────────────────────────────── */}
          <Box>
            <Heading size="lg" color={textPrimary} mb={2}>The experience</Heading>
            <Text color={textSecondary} mb={5} maxW="3xl">
              No dashboards to learn, no technical setup. The owner opens one page on their own site and just talks.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <Feature icon={FiMessageCircle} title="1 · Just chat" color="blue">
                A single chat box on their own tenant site (voice too, on phone). They ask in plain English —
                or speak it.
              </Feature>
              <Feature icon={FiZap} title="2 · It does the work" color="purple">
                An agent harness runs the back-office in autonomous loops — reads their inbox, roster, jobs and
                books, and drafts every action.
              </Feature>
              <Feature icon={FiCheckCircle} title="3 · You approve" color="green">
                Everything lands one click from done. Nothing sends or spends without the owner's yes. Trust by design.
              </Feature>
            </SimpleGrid>
          </Box>

          {/* ── The engine ─────────────────────────────────── */}
          <Box>
            <Heading size="lg" color={textPrimary} mb={2}>The engine</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              <Feature icon={FiCpu} title="Local + cloud AI, chosen per job">
                Frontier models (Claude) for reliable money & comms work; self-hosted open models on our own
                hardware for privacy-sensitive jobs. The right brain for each task.
              </Feature>
              <Feature icon={FiShield} title="Private by default" color="green">
                Regulated data (e.g. aged-care health information) never leaves our own machines — a compliance
                edge generic AI tools can't match.
              </Feature>
              <Feature icon={FiRepeat} title="Autonomous run-loops" color="blue">
                A Pi agent works each business's operational memory on a daily loop — the "does the work while you
                sleep" layer, already built and running.
              </Feature>
              <Feature icon={FiLayers} title="Vertical templates">
                Hard-won industry depth — aged-care rostering & admissions, community-org ops — cloned per client.
                Every client gets their HQ, on Autopilot.
              </Feature>
            </SimpleGrid>
          </Box>

          <Divider borderColor={cardBorder} />

          {/* ── Hardware & costs ───────────────────────────── */}
          <Box>
            <Heading size="lg" color={textPrimary} mb={2}>The hardware, and what it really costs</Heading>
            <Text color={textSecondary} mb={2} maxW="3xl">
              Running the largest open models (a 753B-parameter reasoning model) at full effort needs roughly a
              terabyte of fast memory. Here are the honest paths and Australian prices (July 2026).
            </Text>
            <Text fontSize="sm" color={textMuted} mb={5} maxW="3xl">
              Note: a 2026 global memory shortage has ~doubled RAM prices and made some options unbuyable new —
              a key reason the plan is to <strong>rent before we buy</strong>.
            </Text>
            <TableContainer border="1px solid" borderColor={cardBorder} borderRadius="xl" bg={cardBg}>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr>
                    <Th>Path</Th><Th>Memory</Th><Th isNumeric>AU cost (now)</Th><Th>Speed</Th><Th>Fits an office?</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr>
                    <Td><strong>Apple cluster</strong><br /><Text fontSize="xs" color={textMuted}>2× Mac Studio 512GB</Text></Td>
                    <Td>~1 TB</Td><Td isNumeric>~A$30k*</Td><Td>~25–32 tok/s</Td><Td>✅ silent</Td>
                  </Tr>
                  <Tr>
                    <Td><strong>EPYC big-RAM box</strong><br /><Text fontSize="xs" color={textMuted}>768GB–1TB RAM + 1 GPU</Text></Td>
                    <Td>~1 TB</Td><Td isNumeric>~A$45–56k</Td><Td>slow (5–14 tok/s)</Td><Td>✅ one box</Td>
                  </Tr>
                  <Tr>
                    <Td><strong>VRAM tower</strong><br /><Text fontSize="xs" color={textMuted}>4× RTX PRO 6000</Text></Td>
                    <Td>384 GB VRAM</Td><Td isNumeric>~A$88k</Td><Td>fast (40–70 tok/s)</Td><Td>✅ (not 1TB)</Td>
                  </Tr>
                  <Tr>
                    <Td><strong>Datacenter node</strong><br /><Text fontSize="xs" color={textMuted}>8× H200 / MI300X</Text></Td>
                    <Td>1–1.5 TB VRAM</Td><Td isNumeric>A$300–600k</Td><Td>fastest</Td><Td>❌ colo only</Td>
                  </Tr>
                </Tbody>
              </Table>
            </TableContainer>
            <Text fontSize="xs" color={textMuted} mt={2}>*The 512GB Mac Studio is currently unbuyable new (memory shortage); the M5 Ultra with up to 768GB is expected ~Oct 2026.</Text>
          </Box>

          {/* ── The insight ────────────────────────────────── */}
          <Card bg={useColorModeValue("purple.50", "whiteAlpha.100")} borderColor={accent}>
            <HStack mb={2} spacing={3} align="start">
              <Icon as={FiTrendingUp} boxSize={6} color={accent} mt={1} />
              <Heading size="md" color={textPrimary}>The key insight: concurrency, not raw speed</Heading>
            </HStack>
            <Text color={textSecondary} lineHeight="1.7">
              A A$500k datacenter box makes a <em>single</em> answer only ~2.5× faster than a Mac Studio — but
              ~<strong>300× faster in aggregate</strong> (≈6,000 tokens/sec, ~100–200 agents at once). That
              concurrency — one machine running many businesses' back-offices in parallel — is exactly what this
              venture needs. And you can <strong>rent that exact capability by the hour</strong> (~A$25–55/hr),
              spun up only while work runs. So we get the power without the half-million-dollar cheque.
            </Text>
          </Card>

          <Divider borderColor={cardBorder} />

          {/* ── Funding stages ─────────────────────────────── */}
          <Box>
            <Heading size="lg" color={textPrimary} mb={2}>The funding stages</Heading>
            <Text color={textSecondary} mb={6} maxW="3xl">
              Capital follows proof. Each stage unlocks only when a hard trigger fires — so spend always trails
              revenue, never leads it.
            </Text>
            <Stack spacing={4}>
              {[
                { n: "0", tag: "Now", color: "green", cost: "≈ A$0 capex",
                  title: "Prove the loop", trigger: "Trigger to advance: 3–5 paying pilots",
                  body: "Run the back-office loop on existing hardware + frontier API. First pilot (Paradise Lakes aged-care ops) is already live. Measure hours saved and how much lands one-click-from-done." },
                { n: "1", tag: "On demand", color: "blue", cost: "~A$25–55 / hour",
                  title: "Rent big iron by the hour", trigger: "Trigger: a privacy job, or heavy parallel fan-out",
                  body: "Spin up an 8-GPU cloud node only while a run executes — full 300× concurrency, self-hosted open models for private data, zero capex." },
                { n: "2", tag: "Scale", color: "purple", cost: "~A$4–7k / month",
                  title: "Colocate an owned node", trigger: "Trigger: ≥ ~10,000 GPU-hours/mo for 3 months",
                  body: "Once rental spend is durably heavy, buy an 8-GPU server and colocate it in a Brisbane datacenter. Break-even vs renting: ~1.5–3 years." },
                { n: "3", tag: "Compliance", color: "orange", cost: "A$45–56k one-off",
                  title: "Own a private in-house rig", trigger: "Trigger: a regulated client whose data can't leave the box",
                  body: "A ~1TB in-house machine beside the existing Mac Studio for health/legal/financial verticals — self-hosting sold as compliance, and charged for." },
              ].map((s) => (
                <Box key={s.n} bg={cardBg} border="1px solid" borderColor={cardBorder} borderRadius="xl" p={5}>
                  <Stack direction={{ base: "column", md: "row" }} spacing={4} align={{ md: "center" }}>
                    <HStack spacing={4} minW={{ md: "260px" }}>
                      <Box bg={`${s.color}.400`} color="white" borderRadius="lg" w={10} h={10}
                        display="flex" alignItems="center" justifyContent="center" fontWeight="bold" flexShrink={0}>
                        {s.n}
                      </Box>
                      <Box>
                        <HStack><Text fontWeight="bold" color={textPrimary}>{s.title}</Text>
                          <Badge colorScheme={s.color}>{s.tag}</Badge></HStack>
                        <Text fontSize="sm" color={accent} fontWeight="semibold">{s.cost}</Text>
                      </Box>
                    </HStack>
                    <Box>
                      <Text fontSize="sm" color={textSecondary} lineHeight="1.6">{s.body}</Text>
                      <Text fontSize="xs" color={textMuted} mt={1}>{s.trigger}</Text>
                    </Box>
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Box>

          <Divider borderColor={cardBorder} />

          {/* ── The economics ──────────────────────────────── */}
          <Box>
            <Heading size="lg" color={textPrimary} mb={2}>The path to A$60,000 / month</Heading>
            <Text color={textSecondary} mb={5} maxW="3xl">
              Done-for-you AI operations for small businesses is a real, paying market (A$1,500–15,000/mo retainers).
              The price point decides everything — fewer, higher-value customers is the achievable path.
            </Text>
            <TableContainer border="1px solid" borderColor={cardBorder} borderRadius="xl" bg={cardBg}>
              <Table variant="simple" size="sm">
                <Thead>
                  <Tr><Th>Price / month</Th><Th isNumeric>Customers for A$60k</Th><Th>Verdict</Th></Tr>
                </Thead>
                <Tbody>
                  <Tr><Td>A$500</Td><Td isNumeric>120</Td><Td><Badge colorScheme="red">Volume trap</Badge> support breaks first</Td></Tr>
                  <Tr><Td>A$1,500</Td><Td isNumeric>40</Td><Td><Badge colorScheme="yellow">Feasible</Badge> with one helper</Td></Tr>
                  <Tr bg={useColorModeValue("green.50", "whiteAlpha.100")}>
                    <Td><strong>A$3,000</strong></Td><Td isNumeric><strong>20</strong></Td>
                    <Td><Badge colorScheme="green">Target</Badge> solo-viable, low churn</Td></Tr>
                </Tbody>
              </Table>
            </TableContainer>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mt={5}>
              <Feature icon={FiDollarSign} title="Margin is not the constraint" color="green">
                76–98% gross margin at every price. Compute is a rounding error — self-hosting is for privacy, not cost.
              </Feature>
              <Feature icon={FiServer} title="~30–40 customers per person" color="blue">
                With the AI doing the work, the human limit is onboarding & trust — not headcount. ~20 customers is solo-viable.
              </Feature>
              <Feature icon={FiShield} title="The moat" color="purple">
                Vertical depth, data gravity, privacy, and the run-loop layer — not the model, which is a commodity.
              </Feature>
            </SimpleGrid>
            <Text fontSize="sm" color={textMuted} mt={4}>
              Bottom line: A$60k/month is a credible 18–24 month target at ~20 customers × A$3,000, solo-to-two-person.
              Biggest risk is trust & support load — which is why one-click human approval is the core of the product.
            </Text>
          </Box>

          {/* ── Status / CTA ───────────────────────────────── */}
          <Card bg={useColorModeValue("gray.100", "whiteAlpha.100")} textAlign="center">
            <Heading size="md" color={textPrimary} mb={2}>Where it stands</Heading>
            <Text color={textSecondary} maxW="2xl" mx="auto" mb={4}>
              Pilot #1 — the Paradise Lakes aged-care back-office loop — is built and running on existing hardware.
              The plan is to prove it, rent scale on demand, and only buy hardware when the numbers demand it.
            </Text>
            <HStack justify="center" spacing={3} flexWrap="wrap">
              <Button colorScheme="purple" rightIcon={<FiArrowRight />}
                onClick={() => { window.location.href = "tel:+61458944327"; }}>
                Talk to us
              </Button>
              <Link as={RouterLink} to="/businessbuilder" _hover={{ textDecoration: "none" }}>
                <Button variant="outline">The platform behind it</Button>
              </Link>
            </HStack>
          </Card>

          {/* ── Sign-off ───────────────────────────────────── */}
          <Box>
            <Text color={textSecondary}>
              Regards,<br />
              <strong>Dr Thomas Miller</strong><br />
              <Text as="em" color={textMuted}>Tom Miller Services · HQ Autopilot</Text>
            </Text>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default HQAutopilot;
